import React, { useLayoutEffect } from "react";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import { reset, login, logout } from "./features/auth/authSlice";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getSessionToken } from "./utils/Auth/AuthUtils";
import { SHARED_ROUTES } from "./config/routes";

import "@aws-amplify/ui-react/styles.css";
import "./assets/theme.css";

const AuthListener = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useAuthenticator((context) => [context.user]);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { userState } = useSelector(
    (state) => state.auth
  );

  const authListenerCallback = async ({ payload: { event, data } }) => {
    switch (event) {
      case "signIn":
        const signInToken = await getSessionToken();
        if (!signInToken) return;
        //INFO: See useEffect method for Login disptach request handler
        break;
      case "signOut":
        dispatch(logout()).then(() => {
          dispatch(reset());
          navigate("/");
        });
        break;
      case "signIn_failure":
        console.error("Sign in failure");
        break;
      default:
        console.warn(`Unhandled auth event: ${event}`);
        break;
    }
  };

  //intervention to capture auth state when switching between tabs
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      /*
        if the user has signed out in another tab,
        we want to make sure we have a failsafe to rerender the page on this instance to check the auth state
        if the user is unauthenticated, trigger a signout and reroute
      */
      Auth.currentAuthenticatedUser().catch((error) => {
        console.error(error);
      });
    }
  };

  //add screen visibility change listener to capture switching between tabs
  useLayoutEffect(() => {
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    //AWS amp auth event listener
    Hub.listen("auth", authListenerCallback);

    //navigate to index for safety
    //if the user is unauthenticated, trigger logout and reset redux state
    if (authStatus === "unauthenticated") {
      navigate("/");
    }

    //if the user is authenticated, and there's a user instance from Amplify
    if (authStatus === "authenticated" && !userState && user) {
      //send login request to service with token from cognito
      dispatch(login())
        .then((response) => {
          if (response.meta.requestStatus === "fulfilled") {
            //on successful login, navigate user to home
            navigate(SHARED_ROUTES.home + location.search);
          } else {
            throw response.payload;
          }
        })
        .catch(async (error) => {
          console.log("error ", error);
          //failsafe logout on error
          await dispatch(logout()).then(() => {
            dispatch(reset());
            console.error(error);
            window.alert("Error signing in user");
            throw Error("Error signing in user");
          });
        });
    }
  }, [user, userState, authStatus]);

  return <></>;
};

export default AuthListener;
